
import React, { useState, useEffect, useMemo, createContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DragDropContext } from "@hello-pangea/dnd";
import KanbanAPI from './../../api/kanban-board/kanban-api-factory.js';
import KanbanFunctions from '../../api/kanban-board/kanban-functions';
import NewTaskModal from '../../components/reusable-elements/kanban-new-task-modal';
import { NewProject } from '../../components/kanban/new-project';
import { AddNewColumn } from '../../components/kanban/add-new-column';
import { Columns } from '../../components/kanban/columns';
import { ProjectSelector } from '../../components/kanban/project-selector';
import { TaskTypeSelector } from '../../components/kanban/task-type-selector';
import _ from 'lodash';
import { useMsal } from '@azure/msal-react';
import LoadingAnimation from '../../components/loading/loading.jsx';
import { arrayAny } from '../../utils/arrayUtils.js';

function displayBackgroundImage() {
    return { minHeight: '100vh', backgroundImage: 'url(../assets/img/cover/cover-scrum-board.png)', backgroundRepeat: 'no-repeat', backgroundAttachment: 'fixed', backgroundSize: '360px', backgroundPosition: 'right bottom' };
};

export const KanbanContext = createContext();

const KanbanBoard = () => {

    const { id } = useParams();
    const { instance, inProgress, accounts } = useMsal();
    const navigate = useNavigate();

    const [data, setData] = useState([]);
    const [show, setShow] = useState(false);
    const [showNewProject, setShowNewProject] = useState(false);
    const [newColumn, setNewColumn] = useState(false);

    const [selectedProject, setSelectedProject] = useState(null);

    const [showTaskDelete, setShowTaskDelete] = useState(false);

    const [templateData, setTemplateData] = useState([]);
    const [movedItem, setMovedItem] = useState(null);
    const [fetchInProgress, setFetchInProgress] = useState(false);
    const [shouldFetch, setShouldFetch] = useState(true);
    const [allBoard, setAllBoard] = useState(null);
    const [taskTypeFilter, setTaskTypeFilter] = useState(() => {
        return localStorage.getItem('kanban-taskTypeFilter') || 'All';
    });

    const kanbanAPI = useMemo(() => new KanbanAPI(instance, inProgress, accounts, false), [instance, inProgress, accounts]);

    useEffect(() => {
        if (id && arrayAny(data)) {
            setSelectedProject(data.find(p => p.id === id));
        }
    }, [id, data]);

    const filterTasksByType = (items, filter) => {
        if (filter === 'All') return items;
        return items.filter(item => {
            if (filter === 'Inspection') {
                return item.taskType === 'inspection' || templateData?.items?.find(t => t.id === item.templateId)?.isInspection;
            }
            if (filter === 'Maintenance') {
                return item.taskType === 'maintenance';
            }
            return false;
        });
    };

    const createAllBoard = (boards) => {
        if (!arrayAny(boards)) return null;
        
        const allColumns = new Map();
        
        // Collect all unique columns
        boards.forEach(board => {
            board.columns.forEach(column => {
                if (!allColumns.has(column.name)) {
                    allColumns.set(column.name, {
                        columnId: column.columnId,
                        name: column.name,
                        displayOrder: column.displayOrder,
                        items: []
                    });
                }
            });
        });
        
        // Merge items from all boards into corresponding columns
        boards.forEach(board => {
            board.columns.forEach(column => {
                const mergedColumn = allColumns.get(column.name);
                if (mergedColumn) {
                    mergedColumn.items.push(...column.items);
                }
            });
        });
        
        // Sort columns by display order and items by order
        const sortedColumns = Array.from(allColumns.values())
            .sort((a, b) => a.displayOrder - b.displayOrder)
            .map(column => ({
                ...column,
                items: _.orderBy(column.items, ['order'], ['asc'])
            }));
        
        return {
            id: 'all',
            name: 'All',
            columns: sortedColumns
        };
    };

    const getFilteredProject = (project) => {
        if (!project || taskTypeFilter === 'All') return project;
        
        return {
            ...project,
            columns: project.columns.map(column => ({
                ...column,
                items: filterTasksByType(column.items, taskTypeFilter)
            }))
        };
    };

    useEffect(() => {
        function fetchData() {
            kanbanAPI.getProjectData()
                .then(data => {
                    setData(data);
                    if (arrayAny(data)) {
                        for (const board of data) {
                            for (const column of board.columns) {
                                column.items = _.orderBy(column.items, ['order'], ['asc']);
                            }
                        }
                        
                        // Create the "All" board
                        const mergedBoard = createAllBoard(data);
                        setAllBoard(mergedBoard);
                        
                        if (selectedProject) {
                            if (selectedProject.id === 'all') {
                                setSelectedProject(mergedBoard);
                            } else {
                                setSelectedProject(data.find(p => p.projectId === selectedProject.projectId));
                            }
                        }
                        else {
                            // Try to restore from localStorage
                            const savedProjectId = localStorage.getItem('kanban-selectedProjectId');
                            if (savedProjectId === 'all') {
                                setSelectedProject(mergedBoard);
                            } else if (savedProjectId) {
                                const savedProject = data.find(p => p.id === savedProjectId);
                                setSelectedProject(savedProject || mergedBoard);
                            } else {
                                setSelectedProject(mergedBoard);
                            }
                        }
                    }
                    setTemplateData(KanbanAPI.templateData);
                })
                .finally(() => {
                    setFetchInProgress(false);
                    setShouldFetch(false);
                })

        };
        if (shouldFetch && !fetchInProgress) {
            setFetchInProgress(true);
            fetchData();
        }
    }, [shouldFetch, fetchInProgress, kanbanAPI]);

    // useEffect(() => {
    //     kanbanAPI.listenForUpdates("columns", (data) => { // Receive data from server via socket.io after updates applied
    //         setData(data);
    //         rerender(!render);
    //     });
    //     kanbanAPI.listenForUpdates("newProject", (project) => { // Receive data from server via socket.io after updates applied
    //         setSelectedProject(project);
    //         rerender(!render);
    //     });
    // });



    const handleOnDragStart = ({ source }) => {
        const sourceColumn = selectedProject.columns.find(t => t.name === source.droppableId);
        const tile = sourceColumn.items[source.index];
        tile.saving = true;
        setMovedItem(tile);
    };

    const handleDragEnd = async ({ destination, source }) => {
        if (!destination || !movedItem) {
            return;
        }

        if (destination.index === source.index && destination.droppableId === source.droppableId) {
            setMovedItem(current => current.saving = false);
            return;
        }

        setSelectedProject(current => {

            const sourceColumn = current.columns.find(t => t.name === source.droppableId);
            const destinationColumn = current.columns.find(t => t.name === destination.droppableId);
            const isLastColumn = _.maxBy(current.columns, c => c.displayOrder).columnId === destinationColumn.columnId;

            movedItem.order = destination.index;

            destinationColumn.items = _.orderBy(destinationColumn.items, ['order'], ['asc']);
            sourceColumn.items = _.orderBy(sourceColumn.items, ['order'], ['asc']);

            if (sourceColumn !== destinationColumn) {
                movedItem.destinationColumnId = destinationColumn.columnId;

                // Handle completion status based on column
                if (isLastColumn) {
                    movedItem.completed = new Date().toISOString();
                    movedItem.status = "Closed";
                } else {
                    movedItem.completed = null;
                    movedItem.status = destinationColumn.name === "In Progress" ? "In Progress" : "Open";
                }

                const tileTemplate = templateData.items.find(t => t.id === movedItem.templateId);

                if (tileTemplate && arrayAny(tileTemplate.subTasks)) {
                    const template = tileTemplate.subTasks.find(st => st.displayOrder === destinationColumn.displayOrder);
                    if (template && arrayAny(template.items)) {
                        let newSubTasks = _.orderBy(tileTemplate.subTasks.find(st => st.displayOrder === destinationColumn.displayOrder).items, st => st.order, ['asc']);
                        newSubTasks = _.differenceBy(newSubTasks, movedItem.subTasks, 'subTaskId');
                        movedItem.subTasks.push(...newSubTasks);
                    }
                }
                sourceColumn.items.splice(sourceColumn.items.indexOf(movedItem), 1);
                destinationColumn.items.splice(destination.index, 0, movedItem);
            }
            else {
                destinationColumn.items.splice(destinationColumn.items.indexOf(movedItem), 1);
                destinationColumn.items.splice(destination.index, 0, movedItem);
            }

            sourceColumn.items.forEach((item, index) => {
                item.order = index;
            });

            destinationColumn.items.forEach((item, index) => {
                item.order = index;
            });

            return current;
        });

        const data = await kanbanAPI.upsertTile(movedItem, "All");
        updateBoards(data);
        setMovedItem(null);
    };

    const handleShow = () => setShow(true);
    const handleCancel = () => setShow(false);
    const handleAddColumn = () => setNewColumn(true);
    const handleCancelNewColumn = () => setNewColumn(false);
    const handleShowTaskDelete = () => setShowTaskDelete(!showTaskDelete);

    const handleSaveColumn = (data) => {
        setNewColumn(false);
        kanbanAPI.addColumnToProject(data.selectedProject, data.columnName);
    }

    const handleAddNewTaskToFirstColumn = async (title, description, selectedTemplate, selectedProject) => {

        console.log(`Task Title: ${title}`);
        console.log(`Task Description: ${description}`);
        console.log(`Selected Template: ${selectedTemplate.templateName}`);

        const project = data.find(d => d.id === selectedProject.id);

        const firstColumn = KanbanFunctions.getFirstColumn(project.columns);

        selectedTemplate.subTasks = selectedTemplate.subTasks ?? [];

        const newTasks = KanbanFunctions.getTasksForColumnFromTemplateTasks(firstColumn, selectedTemplate.subTasks);

        for (const subTask of newTasks) {
            subTask.id = KanbanFunctions.getRandomId();
        }

        if (title) {
            const data = await kanbanAPI.addNewTask(project, title, description, accounts[0], selectedTemplate, newTasks);

            if (data && arrayAny(Object.keys(data))) {
                setData(current => {
                    const board = current.find(b => b.id === data.id);
                    current.splice(current.indexOf(board), 1, data);
                    return current;
                });
                setSelectedProject(data);
            }
            setShow(false);
        }
        else {
            console.log("Will not save an empty task with no title.");
            // TODO toastr a warning?
        }
    }



    const handleNewProjectClick = () => setShowNewProject(true);
    const handleCancelNewProject = () => setShowNewProject(false);
    const handleAddNewProject = async projectName => {

        const data = await kanbanAPI.addProject(projectName);
        setShowNewProject(false);
        updateBoards(data);
    }

    const handleDeleteSelectedTasks = async board => {

        setShowTaskDelete(false);
        const tilesToDelete = _.filter(_.flatMap(board.columns, c => c.items), i => i.shouldDelete);

        for (const tile of tilesToDelete) {
            const column = board.columns.find(c => c.columnId === tile.columnId);
            column.items.splice(column.items.indexOf(tile), 1);
        }

        setSelectedProject(board);
        const data = await kanbanAPI.deleteTiles(tilesToDelete);
        var newBoard = data.find(b => b.id === board.id);
        updateBoards(newBoard, setData, setSelectedProject);
    }

    const updateBoards = newData => {
        if (newData && arrayAny(Object.keys(newData))) {

            if (!!movedItem) {
                for (const column of newData.columns) {

                    const movingTileIndex = column.items.findIndex(t => t.id === movedItem.id);
                    const movingTile = column.items.find(t => t.id === movedItem.id);

                    if (!!movingTile) {
                        movingTile.saving = false;
                        setMovedItem(movingTile);
                        column.items.splice(movingTileIndex, 1, movingTile);
                        break;
                    }
                }
            }

            setSelectedProject(newData);
            setData(current => {
                if (current && Array.isArray(current)) {
                    const boardIndex = current.findIndex(b => b.id === newData.id);

                    if (boardIndex > -1) {
                        current.splice(boardIndex, 1);
                        current.splice(boardIndex, 0, newData);
                    }
                }
                else {
                    current = [newData];

                }
                return current;
            });
        }
    }

    const localUpsertTile = async tile => {
        const data = await kanbanAPI.upsertTile(tile, "All");
        updateBoards(data);
    }

    const localDeleteSubTask = async (project, tileId, subTask) => {
        // Find and update tile locally
        setData(current => {
            const board = current.find(b => b.id === project.id);
            const tile = board.columns.flatMap(c => c.items).find(t => t.id === tileId);
            if (tile && tile.subTasks) {
                tile.subTasks = tile.subTasks.filter(st => st.subTaskId !== subTask.subTaskId);
            }
            return current;
        });
        setSelectedProject(current => {
            const tile = current.columns.flatMap(c => c.items).find(t => t.id === tileId);
            if (tile && tile.subTasks) {
                tile.subTasks = tile.subTasks.filter(st => st.subTaskId !== subTask.subTaskId);
            }
            return current;
        });
    }

    const localAddSubTask = async (project, tileId, newTask) => {
        // Generate unique ID once
        newTask.subTaskId = KanbanFunctions.getRandomId();
        
        // Find and update tile locally
        setData(current => {
            const board = current.find(b => b.id === project.id);
            const tile = board.columns.flatMap(c => c.items).find(t => t.id === tileId);
            if (tile) {
                newTask.order = tile.subTasks?.length || 0;
                tile.subTasks = tile.subTasks || [];
                tile.subTasks.push(newTask);
            }
            return current;
        });
        setSelectedProject(current => {
            const tile = current.columns.flatMap(c => c.items).find(t => t.id === tileId);
            if (tile) {
                tile.subTasks = tile.subTasks || [];
                // Only add if not already present
                if (!tile.subTasks.find(st => st.subTaskId === newTask.subTaskId)) {
                    tile.subTasks.push(newTask);
                }
            }
            return current;
        });
    }

    const localUpdateSubTask = async (project, tileId, updatedTask) => {
        // Find and update tile locally
        setData(current => {
            const board = current.find(b => b.id === project.id);
            const tile = board.columns.flatMap(c => c.items).find(t => t.id === tileId);
            if (tile && tile.subTasks) {
                const index = tile.subTasks.findIndex(st => st.subTaskId === updatedTask.subTaskId);
                if (index !== -1) {
                    tile.subTasks[index] = updatedTask;
                }
            }
            return current;
        });
        setSelectedProject(current => {
            const tile = current.columns.flatMap(c => c.items).find(t => t.id === tileId);
            if (tile && tile.subTasks) {
                const index = tile.subTasks.findIndex(st => st.subTaskId === updatedTask.subTaskId);
                if (index !== -1) {
                    tile.subTasks[index] = updatedTask;
                }
            }
            return current;
        });
    }

    const localMoveColumn = async (project, column, direction) => {
        const data = await kanbanAPI.moveColumn(project, column, direction);
        updateBoards(data);
    };

    const localDeleteColumn = async (board, column) => {

        if (column.displayOrder === 0 || board.columns.length <= 1) { return; } // Don't delete first column. We need somewhere for tasks to go when we delete a column

        const data = await kanbanAPI.deleteColumn(board, column);
        updateBoards(data);
    };

    const localMarkTileToDelete = tile => {
        const thisBoard = data.find(b => b.id === tile.boardId);
        const thisColumn = thisBoard.columns.find(c => c.columnId === tile.columnId);
        const thisTile = thisColumn.items.find(i => i.id === tile.id);

        thisTile.shouldDelete = true;

        setSelectedProject(thisBoard);
    };

    const childSetData = updateMethod => {

        setData(current => {
            const newState = updateMethod(current);
            const updatedBoard = newState.find(b => b.id === selectedProject.id);
            setSelectedProject(updatedBoard);
            return newState;
        });
    };

    return (
        <KanbanContext.Provider value={{
            setData: childSetData,
            upsertTile: localUpsertTile,
            deleteSubTask: localDeleteSubTask,
            addSubTask: localAddSubTask,
            updateSubTask: localUpdateSubTask,
            moveColumn: localMoveColumn,
            deleteColumn: localDeleteColumn,
            markTileToDelete: localMarkTileToDelete
        }}>
            <div style={displayBackgroundImage()}>

                <NewProject
                    show={showNewProject}
                    handleCancel={handleCancelNewProject}
                    handleSave={handleAddNewProject}
                />

                <NewTaskModal
                    show={show}
                    handleCancel={handleCancel}
                    handleSave={handleAddNewTaskToFirstColumn}
                    selectedProject={selectedProject}
                    templateData={templateData}
                />

                <AddNewColumn
                    show={newColumn}
                    handleCancel={handleCancelNewColumn}
                    handleSave={e => handleSaveColumn(e)}
                    selectedProject={selectedProject}
                />

                <div className="d-flex align-items-center mb-3">
                    <div>
                        <ol className="breadcrumb">
                            <li className="breadcrumb-item"><a href="#/">Home</a></li>
                            <li className="breadcrumb-item active">Boards </li>
                        </ol>
                        <h1 className="page-header mb-0">
                            Kanban Board
                        </h1>
                    </div>
                </div>

                <div className="mb-3 p-3 bg-light rounded">
                    <div className="d-flex align-items-center gap-4">
                        {selectedProject && (
                            <div className="dropdown">
                                <button type="button" className="btn btn-primary btn-sm" data-bs-toggle="dropdown">
                                    Action&nbsp;&nbsp;<span className="caret"></span>
                                </button>
                                <div className="dropdown-menu">
                                    <div className='dropdown-item link-like-div' onClick={handleShow}><i className='fa fa-plus fa-lg' ></i>&nbsp;&nbsp;Add Task</div>
                                    <div className='dropdown-item link-like-div' onClick={handleShowTaskDelete}><i className='fa fa-trash fa-lg' ></i>&nbsp;&nbsp;{`${showTaskDelete ? 'Cancel Delete' : 'Delete Tasks'}`}</div>
                                    {showTaskDelete &&
                                        <div className='dropdown-item link-like-div' onClick={() => handleDeleteSelectedTasks(selectedProject)} ><i className='fa fa-trash fa-lg' ></i>&nbsp;&nbsp;Delete Selected Tasks</div>
                                    }
                                    <div className="dropdown-divider"></div>
                                    <div className='dropdown-item link-like-div' onClick={handleAddColumn}><i className='fa fa-plus fa-lg' ></i>&nbsp;&nbsp;Add Column</div>
                                </div>
                            </div>
                        )}
                        <div className="d-flex align-items-center gap-2">
                            <label className="form-label mb-0 fw-bold">Board:</label>
                            <ProjectSelector
                                projects={KanbanFunctions.pullProjectNames(data)}
                                allBoard={allBoard}
                                onProjectSelect={board => {
                                    if (board.id === 'all') {
                                        setSelectedProject(allBoard);
                                        localStorage.setItem('kanban-selectedProjectId', 'all');
                                    } else {
                                        const selectedBoard = data.find(b => b.id === board.id);
                                        setSelectedProject(selectedBoard);
                                        localStorage.setItem('kanban-selectedProjectId', board.id);
                                    }
                                }}
                                selectedProject={selectedProject}
                                handleNewProjectClick={handleNewProjectClick}
                                compact={true}
                            />
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <label className="form-label mb-0 fw-bold">Task Type:</label>
                            <TaskTypeSelector 
                                taskTypeFilter={taskTypeFilter}
                                onTaskTypeChange={(filter) => {
                                    setTaskTypeFilter(filter);
                                    localStorage.setItem('kanban-taskTypeFilter', filter);
                                }}
                            />
                        </div>
                    </div>
                </div>





                <div className="row">
                    {!shouldFetch && !fetchInProgress ?
                        <DragDropContext onDragEnd={handleDragEnd} onDragStart={handleOnDragStart} >
                            <Columns
                                project={getFilteredProject(selectedProject)}
                                showTaskDelete={showTaskDelete}
                                showPriorityBadge={true}
                                showViewDetailsButton={true}
                                onViewDetails={(tile) => navigate(`/kanban/${selectedProject?.id}/task/${tile.id}`)}
                            />
                        </DragDropContext>
                        :
                        <LoadingAnimation />
                    }
                </div>
            </div>
        </KanbanContext.Provider>

    );
};

export default KanbanBoard;


